'use client';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { lazy as lazyProductLightbox, Suspense as ProductLightboxBoundary } from 'react';

const LightboxComponent = lazyProductLightbox(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({ default: module.LightboxComponent })),
);
import { Button } from '@eliadmin/core/src/components/ui/button';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export function ProductImages() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(s => s.product);
  //   const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const images = filerImages(
    templateSettings.product_detail?.gallery_with_image_variants?.enabled ?? false,
    product,
    // selectedVariant,
  );

  const thumbnailImages =
    (images ?? []).map((image: any, i) => (
      <div
        key={image.id ?? image.url ?? i}
        className={cn(
          'flex h-full w-full items-center justify-center rounded-lg bg-card',
          'ea-product-detail-images__carousel-slide',
        )}
      >
        <Image
          alt={resolveImageAlt(image, product?.name)}
          src={image.url}
          width="0"
          height="0"
          sizes="100vw"
          priority={i === 0}
          className={cn(
            'h-full w-full rounded-lg object-contain',
            'ea-product-detail-images__carousel-slide-image',
          )}
        />
      </div>
    )) ?? [];

  const { isOpen, setIsOpen } = useDisclosure();

  return (
    <div className={cn('relative flex h-full max-h-[500px] w-full', 'ea-product-detail-images')}>
      <Button asChild className="absolute left-0 top-0 z-[40]">
        <Link prefetch={false} href={'/produkty'}>
          Späť na produkty
        </Link>
      </Button>
      {isOpen && (
        <ProductLightboxBoundary fallback={null}>
          <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={images} />
        </ProductLightboxBoundary>
      )}
      <Carousel
        showArrows
        showDots
        showThumbnails
        options={{ loop: true }}
        className={{
          main: cn(
            'h-full w-full overflow-hidden rounded-lg border shadow-sm sm:w-[86%]',
            'ea-product-detail-images__carousel-wrapper',
          ),
          slide: cn('rounded-lg', 'ea-product-detail-images__carousel-slide'),
          container: cn('', 'ea-product-detail-images__carousel-container'),
          thumbnail: cn('h-20 w-20 rounded-lg', 'ea-product-detail-images__carousel-thumbnails'),
          thumbnailsWrapper: cn(
            'w-[86px] max-w-[90px] flex-grow',
            'ea-product-detail-images__carousel-thumbnails',
          ),
          thumbnails: cn(
            'w-[86px] max-w-[90px] rounded-lg',
            'ea-product-detail-images__carousel-thumbnails',
          ),
        }}
        slides={thumbnailImages.slice(0, 4)}
        openLightbox={() => setIsOpen(true)}
        showMoreButton={thumbnailImages.length > 4}
        allImagesCount={thumbnailImages.length}
      />
    </div>
  );
}

function filerImages(
  showOnlyVariantImages: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: VariantsEntity,
) {
  const variantImages = Array.isArray(selectedVariant?.images) ? selectedVariant.images : [];
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const images = showOnlyVariantImages && variantImages.some(image => imageSource(image))
    ? variantImages
    : [product?.image, ...productImages, ...variants.flatMap(variant => Array.isArray(variant?.images) ? variant.images : [])];
  return images.filter((image): image is NonNullable<typeof image> => Boolean(imageSource(image)));
}
